// Package Block
import { useEffect, useState } from "react";
import { Badge, Layout, Tooltip } from "antd";
import { shallowEqual, useSelector } from "react-redux";
import { isEmpty } from "lodash";

// Custom Components & Functions Block
import { useSubheader } from "../../_core/MetronicSubheader";
import { BreadCrumbs } from "../subheader/components/BreadCrumbs";
import { NotificationsDrawer } from "../extras/dropdowns/NotificationsDrawer";
import { QuickUserToggler } from "../extras/offcanvas/QuickUserToggler";
import QuickCreate from "./../../../../app/components/PromptIfDirty/QuickCreate/index";
import SwitchUser from "../extras/dropdowns/SwitchUser";
import { ChatbotHelpToggle } from "./ChatbotHelpToggle";
import { EcomChatToggle } from "./EcomChatToggle";
import { KaiHeaderButton } from "../../../../app/components/Kai/KaiWidget";

const { Header } = Layout;

const AppHeader = () => {
  const [isCometChatOpen, setIsCometChatOpen] = useState(false);
  const [hasCometChatAccess, setHasCometChatAccess] = useState(false);
  const [cometChatUnreadCount, setCometChatUnreadCount] = useState(0);
  const { currentProductState } = useSelector(
    (state) => ({ currentProductState: state.products }),
    shallowEqual,
  );
  const { productForEdit } = currentProductState;

  const subheader = useSubheader();

  useEffect(() => {
    setHasCometChatAccess(!!window.__kappCometChatAccess);
    setIsCometChatOpen(!!window.__kappCometChatOpen);
    setCometChatUnreadCount(Number(window.__kappCometChatUnreadCount || 0));

    const handleCometChatAvailability = (event) => {
      const nextAccess = !!event?.detail?.hasAccess;
      setHasCometChatAccess(nextAccess);
      if (!nextAccess) {
        setIsCometChatOpen(false);
        setCometChatUnreadCount(0);
      }
    };

    const handleCometChatState = (event) => {
      const nextOpen = !!event?.detail?.open;
      setIsCometChatOpen(nextOpen);
    };

    const handleCometChatUnread = (event) => {
      const nextCount = Number(event?.detail?.count || 0);
      setCometChatUnreadCount(Number.isFinite(nextCount) && nextCount > 0 ? Math.floor(nextCount) : 0);
    };

    window.addEventListener("kapp:cometchat-availability", handleCometChatAvailability);
    window.addEventListener("kapp:cometchat-state", handleCometChatState);
    window.addEventListener("kapp:cometchat-unread", handleCometChatUnread);

    return () => {
      window.removeEventListener("kapp:cometchat-availability", handleCometChatAvailability);
      window.removeEventListener("kapp:cometchat-state", handleCometChatState);
      window.removeEventListener("kapp:cometchat-unread", handleCometChatUnread);
    };
  }, []);

  const toggleCometChat = () => {
    if (typeof window !== "undefined" && window.Notification?.permission === "default") {
      void window.Notification.requestPermission();
    }
    const nextOpenState = !isCometChatOpen;
    setIsCometChatOpen(nextOpenState);
    window.dispatchEvent(new CustomEvent("kapp:toggle-cometchat", { detail: { open: nextOpenState } }));
  };

  const getHeaderNameForProduct = (name) => {
    if (name?.length) {
      return (
        <Tooltip title={name}>
          <div className="product-name-ellipse">
            {name?.length > 40 ? `${name.slice(0, 41)} ...` : name}
          </div>
        </Tooltip>
      );
    }
    return subheader.title;
  };

  return (
    <Layout id="kt_header" className="layout">
      <Header className="topbar-header">
        <div className="topbar-container">
          <div className="breadcrumb-text">
            <div style={{ alignSelf: "flex-start" }}>
              <BreadCrumbs items={subheader.breadcrumbs} />
            </div>
            <div className="d-flex">
              <h1 className="breadcrumbs-title">
                {!isEmpty(productForEdit) ? getHeaderNameForProduct(productForEdit?.name) : subheader.title}
              </h1>
              {productForEdit?.deleted_at ? <i style={{ color: "red", margin: "auto 4px" }}>Archived</i> : null}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <QuickCreate />
            <div className="ml-4 mr-2">
              <SwitchUser />
            </div>
            {hasCometChatAccess && (
              <Tooltip title={isCometChatOpen ? "Close chat" : "Open chat"}>
                  <button
                    className="ml-2 mr-0 p-0 border-0 bg-transparent position-relative"
                    onClick={toggleCometChat}
                    aria-label={isCometChatOpen ? "Close chat" : "Open chat"}
                  >
                    {cometChatUnreadCount > 0 && (
                      <span className="cometchat-toggle-btn">
                        {cometChatUnreadCount > 99 ? "99+" : cometChatUnreadCount}
                      </span>
                    )}
                    <span className="cursor-pointer m-0">
                      <svg width={26} height={26} viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="12" fill={isCometChatOpen ? "#e81e61" : "#121330"} />
                        <path
                          d="M6.5 14.5L10.5 10L12.8 12.3L17.5 9.5L14 16L11.7 13.7L6.5 14.5Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  </button>
              </Tooltip>
            )}
            <div className="mr-3">
              <NotificationsDrawer />
            </div>
            {/* KHub support chatbot launcher — beside the bell */}
            <KaiHeaderButton />
            <ChatbotHelpToggle />
            <EcomChatToggle />
            <QuickUserToggler />
          </div>
        </div>
      </Header>
    </Layout>
  );
};

export default AppHeader;
